import React from 'react';
import { useHistory } from 'react-router';
import ym from 'react-yandex-metrika';
import SimpleMarkdown, { SingleNodeParserRule, ReactOutputRule, SingleASTNode } from 'simple-markdown';

import { MetrikaParams } from 'common.components/hooks/use-prep-lesson-click';
import { OwnProps as LibraryBannerProps } from 'common.components/library-banner/library-banner';
import StackCard, { Props as StackCardProps } from 'common.components/stack-card/stack-card';
import StackCardsContainer from 'common.components/stack-cards-container/stack-cards-container';

interface ConfigItem extends StackCardProps {
    metrika?: {
        goal_name?: string;
        goal_params: MetrikaParams;
    };
}

export interface StackCardsConfig {
    banner?: LibraryBannerProps;
    items: ConfigItem[];
    titleId?: string;
}

interface _StackCardsConfigNode extends SingleASTNode {
    config: StackCardsConfig;
}

function isStackCardsConfig(config: any): config is StackCardsConfig {
    return (
        typeof config === 'object' &&
        Array.isArray(config.items) &&
        (config.items.length === 0 || config.items.every((item: unknown) => typeof item === 'object'))
    );
}

const StackCardsEmptyConfig = {
    items: [],
};

export const stackCardsRule: { stackCards: SingleNodeParserRule & ReactOutputRule } = {
    stackCards: {
        order: 0,
        // {stackCards}JSON-STRING{/stackCards}
        match: SimpleMarkdown.inlineRegex(/^\{stackCards\}(.*?)\{\/stackCards\}/),
        parse(capture) {
            try {
                const config = JSON.parse(capture[1]);

                if (isStackCardsConfig(config)) {
                    return {
                        config,
                    };
                }

                return {
                    config: StackCardsEmptyConfig,
                };
            } catch (error) {
                console.error(error);
                if (window?.Ya?.Rum?.logError) {
                    window.Ya.Rum.logError({ level: 'error' }, error);
                }

                return {
                    config: StackCardsEmptyConfig,
                };
            }
        },
        react(node) {
            const { config } = node;

            return <RuleComponent config={config} />;
        },
    },
};

const RuleComponent = ({ config }: { config: StackCardsConfig }) => {
    const { banner, items, titleId } = config;
    const history = useHistory();

    const search = history?.location?.search;

    const handleStackCardClick = ({ linkUrl, metrika }: ConfigItem) => {
        history.push(linkUrl);
        ym('reachGoal', metrika?.goal_name || 'teacher_vitrina_click_prepared_lesson', metrika?.goal_params);
    };

    if (items.length === 0) {
        return null;
    }

    const stackCards = items.map((props) => {
        let linkUrl = props.linkUrl;

        // для МЭШ важно сохранения большинства query-параметров
        if (search) {
            linkUrl += linkUrl.includes('?') ? `&${search.slice(1)}` : search;
        }

        return (
            <StackCard
                {...props}
                isLinkPseudo={true}
                key={props.linkTextId}
                linkUrl={linkUrl}
                onClick={() => handleStackCardClick({ ...props, linkUrl })}
            />
        );
    });

    let buttonUrl = banner?.buttonUrl;
    if (search && buttonUrl) {
        buttonUrl += buttonUrl.includes('?') ? `&${search.slice(1)}` : search;
    }

    return (
        // @ts-ignore жалуется, что buttonUrl может быть undefined,
        // но при наличии banner есть и buttonUrl (можно узнать из типов)
        <StackCardsContainer bannerProps={banner ? { ...banner, buttonUrl } : banner} titleId={titleId}>
            {stackCards}
        </StackCardsContainer>
    );
};
